<template>
  <v-dialog v-model="show" max-width="750px" persistent scrollable>
    <v-card style="height: fit-content; position: relative;">
      <v-overlay absolute :value="loading">
        <v-progress-circular indeterminate size="48" color="primary" />
      </v-overlay>
      <validation-observer ref="form" v-slot="observer">
        <v-card-text>
          <v-card-title class="justify-center" :class="$vuetify.breakpoint.xs ? 'text-subtitle-1' : ''">{{ $t('order.validatePaymentsTitle') }}</v-card-title>

          <v-form>
            <div v-for="(line, index) in lines" :key="index" class="mb-3">
              <v-divider v-if="index > 0 && $vuetify.breakpoint.xs" class="mb-3" />
              <v-row align="stretch" dense>
                <!-- Inputs -->
                <v-col>
                  <v-row dense align="start">
                    <!-- Payment method -->
                    <v-col cols="12" sm="4">
                      <validation-provider
                        v-slot="{ errors: methodErrors }"
                        :name="$t('order.paymentMethodLabel')"
                        rules="required"
                      >
                        <v-select
                          v-model="line.payment_method_id"
                          :items="paymentMethodItems"
                          :label="$t('order.paymentMethodLabel') + ' *'"
                          :error-messages="methodErrors[0]"
                          :error="lineErrors[index].qtyExceeded"
                          outlined
                          dense
                          hide-details="auto"
                        />
                      </validation-provider>
                    </v-col>

                    <!-- Reference -->
                    <v-col cols="12" sm="4">
                      <validation-provider
                        v-slot="{ errors: refErrors }"
                        :name="$t('order.paymentReferenceLabel') + ' ' + (index + 1)"
                        rules="identifier"
                      >
                        <v-text-field
                          v-model="line.payment_reference"
                          :label="$t('order.paymentReferenceLabel')"
                          :error-messages="refErrors[0]"
                          :error="isDuplicateReference(index)"
                          outlined
                          dense
                          hide-details="auto"
                        />
                      </validation-provider>
                    </v-col>

                    <!-- Amount -->
                    <v-col cols="12" sm="4">
                      <validation-provider
                        v-slot="{ errors: amountErrors }"
                        :name="$t('order.paymentAmountLabel')"
                        rules="required"
                      >
                        <v-text-field
                          :value="line.payment_amount"
                          :label="$t('order.paymentAmountLabel') + ' *'"
                          :suffix="focusedAmountIndex === index || line.payment_amount !== null ? currencyCode : ''"
                          :error-messages="amountErrors[0]"
                          :error="lineErrors[index].amountExceeded"
                          type="number"
                          :min="0"
                          step="0.01"
                          outlined
                          dense
                          hide-details="auto"
                          @focus="focusedAmountIndex = index"
                          @blur="onAmountBlur(index, $event)"
                          @input="updateLineAmount(index, $event)"
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Delete -->
                <v-col cols="auto" class="d-flex" :style="$vuetify.breakpoint.xs ? '' : 'align-self: flex-start; padding-top: 9px;'">
                  <v-btn
                    icon
                    small
                    color="error"
                    :style="$vuetify.breakpoint.xs ? 'height: 100%; min-width: 36px; width: 36px; padding: 0;' : ''"
                    @click="removeLine(index)"
                  >
                    <v-icon color="error">mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <div v-if="duplicateReferenceError(index)" class="error--text text-caption mt-1 px-1">
                {{ duplicateReferenceError(index) }}
              </div>
              <div v-if="lineErrors[index].qtyExceeded" class="error--text text-caption mt-1 px-1">
                {{ $t('order.paymentQtyExceeded', { max: lineErrors[index].qtyMax }) }}
              </div>
              <div v-if="lineErrors[index].amountMessage" class="error--text text-caption mt-1 px-1">
                {{ $t('order.paymentAmountExceeded', { max: lineErrors[index].amountMax, currency: currencyCode }) }}
              </div>
            </div>
          </v-form>

          <!-- Add line -->
          <div class="mt-1 mb-4">
            <v-btn text color="primary" small class="pl-0" :disabled="hasLineIssues || hasDuplicateReferences || isFullyPaid" @click="addLine">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('order.addPayment') }}
            </v-btn>
          </div>

          <v-divider class="mb-3" />

          <!-- Order total & remaining -->
          <div class="px-2">
            <div class="py-1">
              <span class="text--secondary">{{ $t('order.orderAmount') }} : </span>
              <span class="font-weight-medium">{{ order.total_price | formatPrice(order.currency) }} {{ currencyCode }}</span>
            </div>
            <div class="py-1">
              <span class="text--secondary">{{ $t('order.paidAmount') }} : </span>
              <span class="font-weight-medium" :class="totalPaid > (order.total_price || 0) ? 'warning--text' : totalPaid > 0 ? 'success--text' : ''">
                {{ totalPaid | formatPrice(order.currency) }} {{ currencyCode }}
              </span>
            </div>
            <div class="py-1">
              <span class="text--secondary">{{ $t('order.remainingToValidate') }} : </span>
              <span class="font-weight-medium" :class="remainingAmount > 0 ? 'error--text' : 'success--text'">
                {{ (remainingAmount > 0 ? remainingAmount : 0) | formatPrice(order.currency) }} {{ currencyCode }}
              </span>
            </div>
          </div>

          <!-- Overpayment warning -->
          <v-alert v-if="totalPaid > (order.total_price || 0)" type="warning" dense text class="mt-3 mb-0">
            {{ $t('order.paymentOverpaidWarning') }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-0 mt-2">
          <v-btn
            color="primary"
            style="width: 50%"
            tile
            :disabled="isSubmitDisabled || observer.invalid || loading"
            @click="onSubmit"
          >
            {{ $t('settings.alerts.validate') }}
          </v-btn>
          <v-btn
            class="ml-0"
            color="secondary"
            style="width: 50%"
            tile
            @click="close"
          >
            {{ $t('settings.alerts.cancel') }}
          </v-btn>
        </v-card-actions>
      </validation-observer>
    </v-card>
  </v-dialog>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'

export default {
  name: 'PaymentValidationDialog',
  components: { ValidationObserver, ValidationProvider },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      required: true,
    },
    paymentMethods: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      lines: [{ payment_method_id: null, payment_amount: null, payment_reference: '' }],
      focusedAmountIndex: null,
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    currencyCode() {
      return this.order?.currency?.code || this.order?.currency_code || ''
    },
    priceMultiplier() {
      return this.order?.currency?.minor_unit != null ? Math.pow(10, this.order.currency.minor_unit) : 100
    },
    totalAmount() {
      return this.lines.reduce((sum, l) => sum + (parseFloat(l.payment_amount) || 0), 0)
    },
    remainingAmount() {
      const remaining = (this.order?.total_price || 0) - this.totalAmount * this.priceMultiplier
      return Math.round(remaining)
    },
    totalPaid() {
      return Math.round(this.totalAmount * this.priceMultiplier)
    },
    isFullyPaid() {
      return this.remainingAmount <= 0
    },
    paymentMethodItems() {
      return this.paymentMethods.map((pm, index) => ({
        text: (pm.labels[this.$i18n.locale] || pm.labels.en || '') + (pm.method_id ? ` ${pm.method_id}` : ''),
        value: index,
      }))
    },
    lineErrors() {
      const errors = this.lines.map(() => ({ qtyExceeded: false, qtyMax: null, amountExceeded: false, amountMessage: false, amountMax: null }))

      const groups = {}
      this.lines.forEach((line, i) => {
        if (line.payment_method_id == null) return
        if (!groups[line.payment_method_id]) {
          groups[line.payment_method_id] = { indices: [], totalAmount: 0 }
        }
        groups[line.payment_method_id].indices.push(i)
        groups[line.payment_method_id].totalAmount += parseFloat(line.payment_amount) || 0
      })

      Object.entries(groups).forEach(([methodIndex, group]) => {
        const method = this.paymentMethods[parseInt(methodIndex)]
        if (!method) return

        if (method.max_quantity != null && group.indices.length > method.max_quantity) {
          const lastIndex = group.indices[group.indices.length - 1]
          errors[lastIndex].qtyExceeded = true
          errors[lastIndex].qtyMax = method.max_quantity
        }
        if (method.max_amount != null && group.totalAmount > method.max_amount) {
          group.indices.forEach((i) => {
            errors[i].amountExceeded = true
            errors[i].amountMax = method.max_amount
          })
          errors[group.indices[group.indices.length - 1]].amountMessage = true
        }
      })

      return errors
    },
    hasLineIssues() {
      return (
        this.lines.some((l) => l.payment_method_id == null || l.payment_amount === null) ||
        this.lineErrors.some((e) => e.qtyExceeded || e.amountExceeded)
      )
    },
    hasDuplicateReferences() {
      const refs = this.lines.map((l) => l.payment_reference?.trim()).filter((r) => r)
      return refs.length !== new Set(refs).size
    },
    isSubmitDisabled() {
      return this.remainingAmount > 0 || this.hasLineIssues || this.hasDuplicateReferences || this.lines.length === 0
    },
  },
  methods: {
    addLine() {
      this.lines.push({ payment_method_id: null, payment_amount: null, payment_reference: '' })
    },
    removeLine(index) {
      this.lines.splice(index, 1)
    },
    onAmountBlur(index, event) {
      this.focusedAmountIndex = null
      if (!event.target.value) {
        this.$set(this.lines, index, { ...this.lines[index], payment_amount: null })
      }
    },
    updateLineAmount(index, value) {
      const parsed = parseFloat(value)
      if (isNaN(parsed)) return
      this.$set(this.lines, index, {
        ...this.lines[index],
        payment_amount: parsed,
      })
    },
    isDuplicateReference(index) {
      const ref = this.lines[index].payment_reference?.trim()
      if (!ref) return false
      return this.lines.filter((l) => l.payment_reference?.trim() === ref).length > 1
    },
    duplicateReferenceError(index) {
      const ref = this.lines[index].payment_reference?.trim()
      if (!ref) return null
      const allIndices = this.lines.reduce((acc, l, i) => {
        if (l.payment_reference?.trim() === ref) acc.push(i)
        return acc
      }, [])
      if (allIndices.length <= 1) return null
      return allIndices[allIndices.length - 1] === index ? this.$t('order.paymentReferenceDuplicate') : null
    },
    close() {
      this.$emit('close')
    },
    async onSubmit() {
      const valid = await this.$refs.form.validate()
      if (!valid || this.isSubmitDisabled) return

      const payments = this.lines.map((line) => {
        const method = this.paymentMethods[line.payment_method_id]
        return {
          ...(method?.method_id ? { payment_method_id: method.method_id } : {}),
          ...(method?.erp_method_id ? { erp_payment_method_id: method.erp_method_id } : {}),
          payment_amount: Math.round((line.payment_amount || 0) * this.priceMultiplier),
          ...(line.payment_reference?.trim() ? { payment_reference: line.payment_reference.trim() } : {}),
        }
      })
      this.$emit('validateForm', payments)
    },
  },
}
</script>
